import { useState, useEffect } from 'react';
import { adminGetTemplates, adminUpdateTemplate } from '../../lib/adminApi';
import AdminSidebar from '../../components/AdminSidebar';

const NAVY   = '#0a2540';
const ORANGE = '#F37428';
const BLUE   = '#0176CE';

export default function AdminEmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [editing, setEditing]     = useState(null);
  const [editorTab, setEditorTab] = useState('edit'); // 'edit' | 'preview'
  const [search, setSearch]       = useState('');
  const [filterType, setFilterType] = useState('all');
  const [saving, setSaving]       = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved]         = useState(false);

  useEffect(() => {
    adminGetTemplates()
      .then(res => { setTemplates(res.templates || res || []); setLoading(false); })
      .catch(e  => { setError(e.message); setLoading(false); });
  }, []);

  function selectTemplate(t) {
    setEditing({ ...t });
    setSaveError('');
    setSaved(false);
    setEditorTab('edit');
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaveError(''); setSaving(true); setSaved(false);
    try {
      await adminUpdateTemplate(editing.template_id, {
        subject:   editing.subject,
        html_body: editing.html_body,
      });
      setTemplates(ts => ts.map(t =>
        t.template_id === editing.template_id
          ? { ...t, subject: editing.subject, html_body: editing.html_body }
          : t
      ));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // Parse variables JSON safely
  function parseVariables(raw) {
    if (!raw) return null;
    try { return typeof raw === 'string' ? JSON.parse(raw) : raw; }
    catch { return null; }
  }

  return (
    <div style={s.page}>
      <AdminSidebar active="email-templates" />
      <main style={s.main}>
        <div style={s.header}>
          <h1 style={s.title}>Email Templates</h1>
          <p style={s.sub}>Manage transactional email content</p>
        </div>

        {loading && <div style={{ color: '#64748b', padding: 32 }}>Loading…</div>}
        {error   && <div style={{ color: '#ef4444', padding: 32 }}>{error}</div>}

        {!loading && !error && (() => {
          // Infer category from template_key
          function inferType(key) {
            if (!key) return 'transactional';
            if (key.includes('marketing') || key.includes('promo') || key.includes('newsletter') || key.includes('campaign')) return 'marketing';
            if (key.includes('product') || key.includes('feature') || key.includes('update') || key.includes('release')) return 'product';
            if (key.includes('onboard') || key.includes('welcome')) return 'onboarding';
            return 'transactional';
          }
          const TYPE_LABELS = { all: 'All', transactional: 'Transactional', onboarding: 'Onboarding', product: 'Product', marketing: 'Marketing' };
          const typeOptions = Object.entries(TYPE_LABELS);
          const typeCounts = templates.reduce((acc, t) => { const ty = inferType(t.template_key); acc[ty] = (acc[ty]||0)+1; return acc; }, {});
          const filtered = templates.filter(t => {
            const matchType = filterType === 'all' || inferType(t.template_key) === filterType;
            const matchSearch = !search || t.name?.toLowerCase().includes(search.toLowerCase()) || t.template_key?.toLowerCase().includes(search.toLowerCase());
            return matchType && matchSearch;
          });
          return (
          <div style={s.layout}>
            {/* Template list */}
            <div style={{ ...s.list, overflow: 'visible' }}>
              {/* Search */}
              <div style={{ padding: '10px 12px', borderBottom: '1px solid #f1f5f9' }}>
                <input
                  style={{ ...s.input, fontSize: 12, padding: '7px 10px' }}
                  placeholder="Search templates…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              {/* Type filter tabs */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
                {typeOptions.map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilterType(key)}
                    style={{
                      background: filterType === key ? NAVY : 'transparent',
                      border: `1px solid ${filterType === key ? NAVY : '#e2e8f0'}`,
                      borderRadius: 5,
                      color: filterType === key ? '#fff' : '#64748b',
                      cursor: 'pointer',
                      fontSize: 10,
                      fontWeight: 600,
                      padding: '3px 8px',
                    }}
                  >
                    {label}{key !== 'all' && typeCounts[key] ? ` (${typeCounts[key]})` : key === 'all' ? ` (${templates.length})` : ' (0)'}
                  </button>
                ))}
              </div>
              {/* List */}
              {filtered.map(t => (
                <div
                  key={t.template_id}
                  style={{
                    ...s.listItem,
                    background:  editing?.template_id === t.template_id ? 'rgba(1,118,206,0.06)' : '#fff',
                    borderLeft:  editing?.template_id === t.template_id ? `3px solid ${BLUE}` : '3px solid transparent',
                  }}
                  onClick={() => selectTemplate(t)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ color: NAVY, fontWeight: 600, fontSize: 13 }}>{t.name}</span>
                    <span style={{ background: 'rgba(1,118,206,0.08)', color: BLUE, borderRadius: 4, fontSize: 9, fontWeight: 700, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{inferType(t.template_key)}</span>
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{t.template_key}</div>
                  <div style={{ color: '#cbd5e1', fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</div>
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ color: '#94a3b8', fontSize: 13, padding: 16 }}>No templates found</div>
              )}
            </div>

            {/* Editor panel */}
            <div style={s.editor}>
              {editing ? (
                <form onSubmit={handleSave}>
                  {/* Editor header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <h2 style={{ color: NAVY, fontSize: 16, fontWeight: 700, margin: '0 0 2px' }}>{editing.name}</h2>
                      <span style={{ color: '#94a3b8', fontSize: 11 }}>{editing.template_key}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {saved && <span style={{ color: '#059669', fontSize: 12, fontWeight: 500 }}>✓ Saved</span>}
                      <button type="submit" style={s.btnPrimary} disabled={saving}>
                        {saving ? 'Saving…' : 'Save Changes'}
                      </button>
                    </div>
                  </div>

                  {saveError && <div style={s.errorBox}>{saveError}</div>}

                  {/* Subject */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={s.label}>Subject Line</label>
                    <input
                      style={s.input}
                      value={editing.subject || ''}
                      onChange={e => setEditing(ed => ({ ...ed, subject: e.target.value }))}
                      required
                    />
                  </div>

                  {/* Variables info box */}
                  {(() => {
                    const vars = parseVariables(editing.variables);
                    if (!vars) return null;
                    const list = Array.isArray(vars) ? vars : Object.keys(vars);
                    if (list.length === 0) return null;
                    return (
                      <div style={s.varsBox}>
                        <span style={{ fontWeight: 600, marginRight: 8 }}>Available variables:</span>
                        {list.map(v => (
                          <code key={v} style={s.varChip}>{`{{${v}}}`}</code>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Edit / Preview tab bar */}
                  <div style={s.editorTabBar}>
                    <button
                      type="button"
                      onClick={() => setEditorTab('edit')}
                      style={{
                        ...s.editorTabBtn,
                        borderBottom:  editorTab === 'edit' ? `2px solid ${BLUE}` : '2px solid transparent',
                        color:         editorTab === 'edit' ? BLUE : '#64748b',
                        fontWeight:    editorTab === 'edit' ? 600 : 400,
                      }}
                    >Edit HTML</button>
                    <button
                      type="button"
                      onClick={() => setEditorTab('preview')}
                      style={{
                        ...s.editorTabBtn,
                        borderBottom:  editorTab === 'preview' ? `2px solid ${BLUE}` : '2px solid transparent',
                        color:         editorTab === 'preview' ? BLUE : '#64748b',
                        fontWeight:    editorTab === 'preview' ? 600 : 400,
                      }}
                    >Preview</button>
                  </div>

                  {/* Edit mode */}
                  {editorTab === 'edit' && (
                    <textarea
                      style={{
                        ...s.input,
                        minHeight: 500,
                        resize: 'vertical',
                        fontFamily: "'Fira Mono', 'Consolas', 'Monaco', monospace",
                        fontSize: 12,
                        lineHeight: 1.6,
                        display: 'block',
                        marginTop: 0,
                      }}
                      value={editing.html_body || ''}
                      onChange={e => setEditing(ed => ({ ...ed, html_body: e.target.value }))}
                      spellCheck={false}
                    />
                  )}

                  {/* Preview mode */}
                  {editorTab === 'preview' && (
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', marginTop: 0 }}>
                      {editing.html_body ? (
                        <iframe
                          title="email-preview"
                          srcDoc={editing.html_body}
                          style={{ width: '100%', minHeight: 500, border: 'none', display: 'block' }}
                          sandbox="allow-same-origin"
                        />
                      ) : (
                        <div style={{ color: '#94a3b8', fontSize: 13, padding: 32, textAlign: 'center' }}>
                          No HTML content to preview.
                        </div>
                      )}
                    </div>
                  )}
                </form>
              ) : (
                <div style={{ color: '#94a3b8', fontSize: 13, padding: 32, textAlign: 'center' }}>
                  Select a template on the left to edit it.
                </div>
              )}
            </div>
          </div>
          );
        })()}
      </main>
    </div>
  );
}

const s = {
  page:        { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:        { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  header:      { marginBottom: 28 },
  title:       { color: NAVY, fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: '-0.4px' },
  sub:         { color: '#64748b', fontSize: 13, margin: '4px 0 0' },
  layout:      { display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'start' },
  list:        { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  listItem:    { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', transition: 'background 0.1s' },
  editor:      { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  label:       { display: 'block', color: '#64748b', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 },
  input:       { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, color: NAVY, fontSize: 14, padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  varsBox:     { background: 'rgba(1,118,206,0.05)', border: '1px solid rgba(1,118,206,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: NAVY, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 },
  varChip:     { background: 'rgba(1,118,206,0.1)', color: BLUE, borderRadius: 4, padding: '1px 6px', fontSize: 11, fontFamily: 'monospace' },
  editorTabBar:{ display: 'flex', borderBottom: '1px solid #e2e8f0', marginBottom: 14 },
  editorTabBtn:{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 13, padding: '8px 16px', transition: 'all 0.12s' },
  errorBox:    { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#dc2626', fontSize: 13, padding: '10px 14px', marginBottom: 16 },
  btnPrimary:  { background: ORANGE, border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '9px 18px' },
};
